/**
 * @license
 * Copyright (c) 2017, Barclays Africa Technology, All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file
 */

import { Injectable } from '@angular/core';
import { Action } from '@ngrx/store';
import { Route } from '@angular/router';
import { go } from '@ngrx/router-store';
import { Effect, Actions, toPayload } from '@ngrx/effects';
import { Router } from '@angular/router';

import { Observable } from 'rxjs/Observable';
import '@api/rxjs-operators';

import * as ui from '@api/store/actions/ui.actions';
import { Menu } from '@api/models/menu';
import { User } from '@api/models/user';
import { Widget } from '@api/models/widget';
import { Dashboard } from '@api/models/dashboard';
import { MenuService, RouterService, DashboardService, ThemeService } from '@api/services';

import { ToastsManager } from 'ng2-toastr';

/**
 * Effects offer a way to isolate and easily test side-effects within your
 * application.
 * The `toPayload` helper function returns just
 * the payload of the currently dispatched action, useful in
 * instances where the current state is not necessary.
 *
 * Documentation on `toPayload` can be found here:
 * https://github.com/ngrx/effects/blob/master/docs/api.md#topayload
 *
 * If you are unfamiliar with the operators being used in these examples, please
 * check out the sources below:
 *
 * Official Docs: http://reactivex.io/rxjs/manual/overview.html#categories-of-operators
 * RxJS 5 Operators By Example: https://gist.github.com/btroncone/d6cf141d6f2c00dc6b35
 */
@Injectable()
export class UiEffects {

    constructor(
        private actions$: Actions,
        private menuService: MenuService,
        private routerService: RouterService,
        private dashboardService: DashboardService,
        private themeService: ThemeService,
        private toastsManager: ToastsManager,
        private router: Router
    ) { }
    
    /**
     * 
     * @param title 
     * @param message 
     */
    getToastContent(title: string, message?: string): string {
        if (message) {
            return `<div class="toaster-content"><div class="toast-title">${title}</div><div><span class="toast-message">${message}</span></div></div></div>`;
        } else {
            return `<div class="toaster-content"><div class="toast-title">${title}</div></div></div>`;
        }
    }

    /**
     * Load routes
     */
    @Effect()
    loadRoute$: Observable<Action> = this.actions$
        .ofType(ui.LOAD_ROUTE)
        .map(toPayload)
        .switchMap((user: User) => this.routerService.getRoutes())
        .delay(300)
        .do((routes: Route[]) => {
            this.routerService.setRoutes(routes);
        })
        .map((routes: Route[]) => new ui.LoadRouteSuccessAction(routes));

    /**
     * Load menu
     */
    @Effect()
    loadMenu$: Observable<Action> = this.actions$
        .ofType(ui.LOAD_MENU)
        .map(toPayload)
        .switchMap((user: User) => this.menuService.getMenus(user))
        .map((menus: Menu[]) => new ui.LoadMenuSuccessAction(menus));

    /**
     * Load dashboard menu
     */
    @Effect()
    loadDashboardMenu$: Observable<Action> = this.actions$
        .ofType(ui.LOAD_DASHBOARD_MENU)
        .switchMap(() => this.dashboardService.getDashboardMenu())
        .map((response: any) => { return (response) })
        .map((dashboards: Dashboard[]) => {
            return new ui.LoadDashboardMenuSuccessAction(dashboards);
        })

    /**
     * Load dashboard
     */
    @Effect()
    loadDashboard$: Observable<Action> = this.actions$
        .ofType(ui.LOAD_DASHBOARD)
        .map(toPayload)
        .switchMap((url: string) => this.dashboardService.getDashboard(url))
        .map((widgets: Widget[]) => new ui.LoadDashboardSuccessAction(widgets));

    /**
     * Redirect on last route
     */
    @Effect()
    redirectTo$: Observable<Action> = this.actions$
        .ofType(ui.LOAD_ROUTE_SUCCESS)
        .map(toPayload)
        .map((routes: Route[]) => {
            return this.routerService.hasRestoreRoute
        });

    /**
     * Goto theme
     */
    @Effect()
    gotoTheme$: Observable<Action> = this.actions$
        .ofType(ui.GOTO_THEME)
        .map(toPayload)
        .switchMap((theme: string) => this.themeService.changeThemeTo(theme))        
        .map((theme: string) => new ui.GotoThemeSuccessAction(theme))
        .catch((error, caught) => Observable.of(new ui.GotoThemeFailAction(error)));

    /**
     * Show Toast
     */
    @Effect()
    showToast$: Observable<Action> = this.actions$
        .ofType(ui.SHOW_TOAST)
        .map(toPayload)
        .do((toastMessage: { message: string, title?: string, type: "info" | "success" | "error" | "warning" }) => {
            switch (toastMessage.type) {
                case "info":
                    this.toastsManager.info(this.getToastContent(toastMessage.message, toastMessage.title), null, null);
                    break;
                case "success":                    
                    this.toastsManager.success(this.getToastContent(toastMessage.message, toastMessage.title), null, null);
                    break;
                case "error":
                    this.toastsManager.error(this.getToastContent(toastMessage.message, toastMessage.title), null, null);
                    break;
                case "warning":
                    this.toastsManager.warning(this.getToastContent(toastMessage.message, toastMessage.title), null, null);
                    break;
            }
        });
}



// WEBPACK FOOTER //
// node_modules/angular2-template-loader!./~/angular-router-loader/src!./src/app/celine/store/effects/ui.effects.ts